import React, { useState, useEffect, useRef } from "react";
import api from "../../Utils/api";
import Spinner from "../Spinner";
import ExportDropdown from "../Calendar/ExportDropdown";

function parseWorkPatterns(shifts) {
  const grouped = {};

  const getHoursDiff = (start, end) =>
    (new Date(end) - new Date(start)) / (1000 * 60 * 60);

  for (const shift of shifts) {
    const location = shift.locationID.name.trim();
    const area = shift.locationID.area;
    const name = shift.userID?.preferredName;

    const start = new Date(shift.startTime);
    const end = new Date(shift.endTime);
    let hours = (end - start) / (1000 * 60 * 60); // Convert ms to hours
    let breakHours = 0;
    if (shift.breakStartTime && shift.breakEndTime) {
      breakHours = getHoursDiff(shift.breakStartTime, shift.breakEndTime);
    }

    hours -= breakHours;

    if (!grouped[location]) grouped[location] = {};
    if (!grouped[location][area]) grouped[location][area] = {};
    if (!grouped[location][area][name])
      grouped[location][area][name] = { shifts: 0, hours: 0 };

    grouped[location][area][name].shifts += 1;
    grouped[location][area][name].hours += hours;
  }

  const workPatterns = Object.entries(grouped).map(([location, areasObj]) => ({
    location,
    areas: Object.entries(areasObj).map(([area, membersObj]) => {
      const members = Object.entries(membersObj).map(
        ([name, { shifts, hours }]) => ({
          name,
          shifts,
          hours: parseFloat(hours.toFixed(2)),
          avgShifts: parseFloat((shifts / 4).toFixed(2)),
          avgHours: parseFloat((hours / 4).toFixed(2)),
        })
      );

      const totalShifts = members.reduce((sum, m) => sum + m.shifts, 0);
      const totalHours = members.reduce((sum, m) => sum + m.hours, 0);

      return {
        area,
        members,
        totalShifts,
        totalHours: parseFloat(totalHours.toFixed(2)),
        avgShifts: parseFloat((totalShifts / 4).toFixed(2)),
        avgHours: parseFloat((totalHours / 4).toFixed(2)),
      };
    }),
  }));

  return workPatterns;
}

function transformTimesheetData(shifts) {
  function formatTime(dateStr) {
    return new Date(dateStr).toISOString().replace("T", " ").slice(0, 19);
  }

  function getHoursDiff(start, end) {
    return (new Date(end) - new Date(start)) / (1000 * 60 * 60);
  }

  return shifts.map((item) => {
    const fullName = item.userID?.preferredName || "";
    const [first, ...rest] = fullName.split(" ");
    const last = rest.join(" ") || "";

    const start = formatTime(item.startTime);
    const end = formatTime(item.endTime);

    const shiftStart = item.timesheetID
      ? formatTime(item.timesheetID.startTime)
      : "";
    const shiftEnd = item.timesheetID
      ? formatTime(item.timesheetID.endTime)
      : "";

    const totalDuration = getHoursDiff(item.startTime, item.endTime);

    let breakDuration = 0;
    if (item.breakStartTime && item.breakEndTime) {
      breakDuration = getHoursDiff(item.breakStartTime, item.breakEndTime);
    }

    const total = (totalDuration - breakDuration).toFixed(2);
    const shiftTotal = getHoursDiff(shiftStart, shiftEnd).toFixed(2);

    return {
      id: item.id,
      member: fullName,
      first,
      last,
      status: item.approved ? "Approved" : "Pending",
      start,
      end,
      total,
      shiftStart: shiftStart,
      shiftEnd: shiftEnd,
      shiftTotal: shiftTotal == NaN ? "" : shiftTotal,
    };
  });
}

function calculateGrandTotals(shifts) {
  // Helper to calculate hours between two dates
  const getHoursDiff = (start, end) =>
    (new Date(end) - new Date(start)) / (1000 * 60 * 60);

  const totalShifts = shifts.length;

  // Calculate total hours worked (subtract breaks if any)
  let totalHours = 0;
  // To count unique members
  const members = new Set();

  shifts.forEach((item) => {
    members.add(item.userID?.id);
    const shiftHours = getHoursDiff(item.startTime, item.endTime);
    let breakHours = 0;
    if (item.breakStartTime && item.breakEndTime) {
      breakHours = getHoursDiff(item.breakStartTime, item.breakEndTime);
    }
    totalHours += shiftHours - breakHours;
  });

  const uniqueMembers = members.size || 1; // avoid divide by zero

  return {
    shifts: totalShifts,
    hours: Number(totalHours.toFixed(2)),
    avgShifts: Number((totalShifts / uniqueMembers).toFixed(2)),
    avgHours: Number((totalHours / uniqueMembers).toFixed(2)),
  };
}

export default function TeamWorkPatterns({ startTime, endTime }) {
  const [shifts, setShifts] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedLocations, setSelectedLocations] = useState(new Set());

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);

  const filteredShifts = shifts
    .filter((s) => selectedUserIds.some((id) => id === s.userID._id))
    .filter((s) => selectedLocationIds.some((id) => id === s.locationID._id));

  const workPatterns = parseWorkPatterns(filteredShifts);
  const timesheetDetails = transformTimesheetData(filteredShifts);
  const grandTotals = calculateGrandTotals(filteredShifts);

  useEffect(() => {
    getSchedules();
  }, [startTime, endTime]);

  const getSchedules = async () => {
    try {
      const start = new Date(startTime); // selectedDate should be a Date or string
      start.setUTCHours(0, 0, 0, 0); // 00:00:00.000

      const end = new Date(endTime); // current date and time
      end.setUTCHours(23, 59, 59, 999); // 23:59:59.999

      // end.setDate(end.getDate() - 1); // add (period - 1) days to include start date
      // end.setUTCHours(23, 59, 59, 999); // Set to 23:59:59.999
      setIsLoading(true);
      const response = await api.post(`/schedule/list`, {
        startTime: start.toISOString(), // full ISO with time
        endTime: end.toISOString(), // full ISO with time
        admin: true,
      });
      setIsLoading(false);

      //   console.log(response.data.schedules);

      setShifts(response.data.schedules);
    } catch (error) {
      console.error(error);
    }
  };

  const mappedUsers = shifts.map((item) => ({
    _id: item.userID._id,
    preferredName: item.userID.preferredName,
  }));

  const mappedLocations = shifts.map((item) => ({
    _id: item.locationID._id,
    name: item.locationID.name,
  }));

  // Make unique (if there are duplicates)
  const uniqueUsers = [...new Set(mappedUsers.map(JSON.stringify))].map(
    JSON.parse
  );
  const uniqueLocations = [...new Set(mappedLocations.map(JSON.stringify))].map(
    JSON.parse
  );

  const flattenedTableData = [];

  workPatterns.forEach((loc) => {
    loc.areas.forEach((area) => {
      // Push area summary row
      flattenedTableData.push({
        location: loc.location,
        area: area.area,
        member: "",
        shifts: area.totalShifts,
        hours: `${area.totalHours} h`,
        avgShifts: area.avgShifts.toFixed(2),
        avgHours: `${area.avgHours.toFixed(2)} h`,
      });

      // Push each member row
      area.members.forEach((member) => {
        flattenedTableData.push({
          location: "",
          area: "",
          member: member.name,
          shifts: member.shifts,
          hours: `${member.hours} h`,
          avgShifts: member.avgShifts.toFixed(2),
          avgHours: `${member.avgHours.toFixed(2)} h`,
        });
      });
    });
  });

  return (
    <div className="p-6 min-h-screen overflow-y-auto -mt-16">
      {isLoading && <Spinner />}

      <div className="flex items-center gap-4  mb-2">
        <MultiSelectDropdown
          options={uniqueUsers}
          label={"Users"}
          selected={selectedUsers}
          setSelected={setSelectedUsers}
          onChange={(ids) => setSelectedUserIds(ids)}
        />
        <MultiSelectDropdown
          options={uniqueLocations}
          label={"Locations"}
          selected={selectedLocations}
          setSelected={setSelectedLocations}
          onChange={(ids) => setSelectedLocationIds(ids)}
        />
      </div>

      {/* Team member work patterns */}
      <div>
        <h2 className="font-semibold text-lg mt-8 mb-4">
          Team member work patterns
        </h2>
        <ExportDropdown data={flattenedTableData} />
        <div className="max-h-[50vh] overflow-y-auto mt-2">
          <table className="min-w-full   border border-gray-200 text-sm">
            <thead className="sticky top-0">
              <tr className="bg-gray-100 text-gray-700">
                <th className="text-left px-2 py-1 border-b">Location</th>
                <th className="text-left px-2 py-1 border-b">Area</th>
                <th className="text-left px-2 py-1 border-b">Team member</th>
                <th className="text-right px-2 py-1 border-b">
                  Total shifts worked
                </th>
                <th className="text-right px-2 py-1 border-b">
                  Total hours worked
                </th>
                <th className="text-right px-2 py-1 border-b">
                  Avg. shifts worked per week
                </th>
                <th className="text-right px-2 py-1 border-b">
                  Avg. hours worked per week
                </th>
              </tr>
            </thead>
            <tbody>
              {workPatterns.map((loc, i) =>
                loc.areas.map((area, j) => (
                  <React.Fragment key={loc.location + area.area}>
                    <tr className="font-semibold">
                      <td className="px-2 py-1 border-b">{loc.location}</td>
                      <td className="px-2 py-1 border-b">{area.area}</td>
                      <td className="px-2 py-1 border-b"></td>
                      <td className="text-right px-2 py-1 border-b">
                        {area.totalShifts}
                      </td>
                      <td className="text-right px-2 py-1 border-b">
                        {area.totalHours} h
                      </td>
                      <td className="text-right px-2 py-1 border-b">
                        {area.avgShifts.toFixed(2)}
                      </td>
                      <td className="text-right px-2 py-1 border-b">
                        {area.avgHours.toFixed(2)} h
                      </td>
                    </tr>
                    {area.members.map((member) => (
                      <tr key={member.name}>
                        <td className="px-2 py-1 border-b"></td>
                        <td className="px-2 py-1 border-b"></td>
                        <td className="px-2 py-1 border-b font-medium">
                          {member.name}
                        </td>
                        <td className="text-right px-2 py-1 border-b">
                          {member.shifts}
                        </td>
                        <td className="text-right px-2 py-1 border-b">
                          {member.hours} h
                        </td>
                        <td className="text-right px-2 py-1 border-b">
                          {member.avgShifts.toFixed(2)}
                        </td>
                        <td className="text-right px-2 py-1 border-b">
                          {member.avgHours.toFixed(2)} h
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
              <tr className="font-bold bg-gray-50">
                <td className="px-2 py-1 border-b" colSpan={3}>
                  Grand total
                </td>
                <td className="text-right px-2 py-1 border-b">
                  {grandTotals.shifts}
                </td>
                <td className="text-right px-2 py-1 border-b">
                  {grandTotals.hours} h
                </td>
                <td className="text-right px-2 py-1 border-b">
                  {grandTotals.avgShifts.toFixed(2)}
                </td>
                <td className="text-right px-2 py-1 border-b">
                  {grandTotals.avgHours.toFixed(2)} h
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Timesheet details */}
      <div className="mt-12 ">
        <h2 className="font-semibold text-lg mb-4">Timesheet details</h2>
        <ExportDropdown data={timesheetDetails} />
        <div className="max-h-[50vh] overflow-y-auto mt-2">
          <table className="min-w-full border border-gray-200 text-sm">
            <thead className="sticky top-0">
              <tr className="bg-gray-100 text-gray-700">
                <th className="px-2 py-1 border-b">Timesheet ID</th>
                <th className="px-2 py-1 border-b">Team member</th>
                <th className="px-2 py-1 border-b">First name</th>
                <th className="px-2 py-1 border-b">Last name</th>
                <th className="px-2 py-1 border-b">Timesheet Status</th>
                <th className="px-2 py-1 border-b">Timesheet Start Time</th>
                <th className="px-2 py-1 border-b">Timesheet End Time</th>
                <th className="px-2 py-1 border-b">Timesheet Total Time</th>
                <th className="px-2 py-1 border-b">Shift Start Time</th>
                <th className="px-2 py-1 border-b">Shift End Time</th>
                <th className="px-2 py-1 border-b">Shift Total Time</th>
              </tr>
            </thead>
            <tbody className="h-[100px]">
              {timesheetDetails.map((row) => (
                <tr key={row.id}>
                  <td className="px-2 py-1 border-b">{row.id}</td>
                  <td className="px-2 py-1 border-b">{row.member}</td>
                  <td className="px-2 py-1 border-b">{row.first}</td>
                  <td className="px-2 py-1 border-b">{row.last}</td>
                  <td className="px-2 py-1 border-b">{row.status}</td>
                  <td className="px-2 py-1 border-b">{row.start}</td>
                  <td className="px-2 py-1 border-b">{row.end}</td>
                  <td className="px-2 py-1 border-b">{row.total}</td>
                  <td className="px-2 py-1 border-b">{row.shiftStart || ""}</td>
                  <td className="px-2 py-1 border-b">{row.shiftEnd || ""}</td>
                  <td className="px-2 py-1 border-b">
                    {row.shiftTotal == "NaN" ? "" : row.shiftTotal}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MultiSelectDropdown({
  label,
  options,
  selected,
  setSelected,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState(true);

  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (options.length > 0 && selected.size === 0 && first) {
      setFirst(false);
      const allIds = options.map((opt) => opt._id);
      const allSelected = new Set(allIds);
      setSelected(allSelected);
      onChange(allIds);
    }
  }, [options, first]);

  const handleChange = (id) => {
    const newSelected = new Set(selected);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    const selectedArray = Array.from(newSelected);
    setSelected(newSelected);
    onChange(selectedArray);
  };

  const handleToggleAll = () => {
    const allIds = options.map((opt) => opt._id);
    const isAllSelected = allIds.every((id) => selected.has(id));

    const newSelected = isAllSelected ? new Set() : new Set(allIds);
    setSelected(newSelected);
    onChange(Array.from(newSelected));
  };

  const isAllSelected =
    options.length > 0 && options.every((opt) => selected.has(opt._id));

  return (
    <div className="space-y-2 w-64">
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          className="w-full border rounded px-3 py-2 flex justify-between items-center text-sm bg-white"
          onClick={() => setOpen((o) => !o)}
        >
          <div className="whitespace-nowrap overflow-hidden text-ellipsis">
            {Array.from(selected).length === 0
              ? `Select ${label}...`
              : (() => {
                  const selectedArray = options.filter((opt) =>
                    selected.has(opt._id)
                  );
                  const maxToShow = 1;
                  const shown = selectedArray.slice(0, maxToShow);
                  const hiddenCount = selectedArray.length - shown.length;
                  return (
                    shown
                      .map((opt) => opt.preferredName || opt.name)
                      .join(", ") +
                    (hiddenCount > 0 ? `, +${hiddenCount} ${label}` : "")
                  );
                })()}
          </div>

          <svg
            className={`w-4 h-4 ml-2 transform ${open ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {open && (
          <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow max-h-60 overflow-auto">
            {/* Select/Deselect All */}
            <label className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer font-medium border-b text-xs">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleToggleAll}
                className="h-3 w-3 accent-blue-500"
              />
              <span className="ml-2">
                {isAllSelected ? "Deselect All" : "Select All"}
              </span>
            </label>

            {/* Individual Items */}
            {options.map((option) => (
              <label
                key={option._id}
                className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(option._id)}
                  onChange={() => handleChange(option._id)}
                  className="h-3 w-3 accent-blue-500"
                />
                <span className="ml-2 text-xs">
                  {option.preferredName || option.name}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
